import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import './SystemLogs.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft,
  FaSearch,
  FaUserCircle,
  FaSignOutAlt,
  FaBell,
  FaServer,
  FaChartBar,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaSyncAlt,
  FaFlask,
  FaGlobe,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaSignInAlt,
  FaBars,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const CATEGORIES = [
  { key: null, label: 'All', icon: FaGlobe },
  { key: 'test', label: 'Tests', icon: FaFlask },
  { key: 'api', label: 'API', icon: FaServer },
  { key: 'error', label: 'Errors', icon: FaExclamationTriangle },
  { key: 'sync', label: 'Syncs', icon: FaExchangeAlt },
  { key: 'login', label: 'Logins', icon: FaSignInAlt },
];

const LEVELS = [null, 'info', 'warn', 'error', 'critical'];

const SystemLogs = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState(null);
  const [level, setLevel] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const refreshRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const limit = 50;

  const loadProfile = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (e) {
      console.error('Error loading profile:', e);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/system-logs/stats'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const offset = page * limit;
      const params = new URLSearchParams({ limit, offset });
      if (category) params.set('category', category);
      if (level) params.set('level', level);
      if (searchTerm) params.set('search', searchTerm);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const res = await fetch(
        getApiEndpoint(`/api/system-logs?${params.toString()}`),
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load system logs');
    } finally {
      setLoading(false);
    }
  }, [page, category, level, searchTerm, startDate, endDate]);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin' && userRole !== 'admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    loadStats();
  }, [navigate, loadProfile, loadStats]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshRef.current = setInterval(() => {
        loadLogs();
        loadStats();
      }, 10000);
    } else if (refreshRef.current) {
      clearInterval(refreshRef.current);
    }
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [autoRefresh, loadLogs, loadStats]);

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatJson = (data) => {
    if (!data) return null;
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return String(data);
    }
  };

  const getStatCount = (cat) => {
    if (!stats?.byCategory) return 0;
    const entry = stats.byCategory.find(c => c.category === cat);
    return entry ? entry.count : 0;
  };

  const getStatusClass = (code) => {
    if (!code) return '';
    if (code >= 200 && code < 300) return 's2xx';
    if (code >= 300 && code < 400) return 's3xx';
    if (code >= 400 && code < 500) return 's4xx';
    return 's5xx';
  };

  const getDurationClass = (ms) => {
    if (!ms) return '';
    if (ms > 5000) return 'very-slow';
    if (ms > 1000) return 'slow';
    return '';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="cs-dashboard">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge">
            <FaServer />
            <span>Cloud Staff Portal</span>
          </div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications">
              <FaBell />
            </button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <Link to="/cloud-staff/dashboard" className="cs-menu-item">
                    <FaChartBar /> Dashboard
                  </Link>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout">
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="system-logs" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />

        <main className="cs-content">
          {/* Page Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #21262d' }}>
            <Link to="/cloud-staff/dashboard" style={{
              width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid #30363d',
              color: '#8b949e', textDecoration: 'none', transition: 'all 0.2s'
            }}>
              <FaArrowLeft />
            </Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>System Logs</h1>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', margin: 0 }}>Every test, API call, error, sync, and login</p>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="sl-stats-bar">
            <div className="sl-stat-card">
              <p className="sl-stat-label">Total Events (24h)</p>
              <p className="sl-stat-value">{stats?.total_24h?.toLocaleString() || '0'}</p>
            </div>
            <div className="sl-stat-card">
              <p className="sl-stat-label">Errors (24h)</p>
              <p className="sl-stat-value errors">{getStatCount('error')}</p>
            </div>
            <div className="sl-stat-card">
              <p className="sl-stat-label">Logins (24h)</p>
              <p className="sl-stat-value logins">{getStatCount('login')}</p>
            </div>
            <div className="sl-stat-card">
              <p className="sl-stat-label">Avg Response (24h)</p>
              <p className="sl-stat-value api">{stats?.avg_duration_ms || 0}<span style={{ fontSize: '0.7rem', color: 'var(--text-2)', fontWeight: 400 }}>ms</span></p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="sl-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key || 'all'}
                className={`sl-tab ${category === cat.key ? 'active' : ''}`}
                onClick={() => { setCategory(cat.key); setPage(0); }}
              >
                <cat.icon style={{ fontSize: '0.7rem' }} />
                {cat.label}
                {cat.key && <span className="sl-tab-count">{getStatCount(cat.key)}</span>}
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div className="sl-toolbar">
            <div className="sl-toolbar-left">
              <div className="sl-search-box">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search actions, messages, emails..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sl-search-input"
                />
              </div>

              <div className="sl-level-pills">
                {LEVELS.map(lvl => (
                  <button
                    key={lvl || 'all'}
                    className={`sl-level-pill ${level === lvl ? (lvl ? `active-${lvl}` : 'active-info') : ''}`}
                    onClick={() => { setLevel(lvl); setPage(0); }}
                  >
                    {lvl || 'All'}
                  </button>
                ))}
              </div>

              <div className="sl-date-range">
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
                  className="sl-date-input"
                  placeholder="From"
                />
                <span className="sl-date-separator">to</span>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
                  className="sl-date-input"
                  placeholder="To"
                />
              </div>
            </div>

            <div className="sl-toolbar-right">
              <button
                className={`sl-refresh-btn ${autoRefresh ? 'auto-active' : ''}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
                title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh (10s)'}
              >
                {autoRefresh && <span className="sl-auto-dot" />}
                <FaSyncAlt style={{ fontSize: '0.75rem' }} />
                {autoRefresh ? 'Live' : 'Auto'}
              </button>
              <span className="sl-total-label">{total.toLocaleString()} entries</span>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="cs-loading">
              <div className="cs-spinner" />
              <p>Loading system logs...</p>
            </div>
          ) : logs.length > 0 ? (
            <>
              <div className="sl-table-wrap">
                <table className="sl-table">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th>Date/Time</th>
                      <th>Category</th>
                      <th>Level</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Endpoint</th>
                      <th>Status</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <React.Fragment key={log.id}>
                        <tr onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                          <td style={{ textAlign: 'center', color: 'var(--text-2)', fontSize: '0.65rem' }}>
                            {expandedId === log.id ? <FaChevronUp /> : <FaChevronDown />}
                          </td>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                            {formatDate(log.created_at)}
                          </td>
                          <td>
                            <span className={`sl-badge cat-${log.category}`}>{log.category}</span>
                          </td>
                          <td>
                            <span className={`sl-badge lvl-${log.level}`}>{log.level}</span>
                          </td>
                          <td style={{ fontSize: '0.78rem' }}>
                            {log.first_name && log.last_name
                              ? `${log.first_name} ${log.last_name}`
                              : log.user_email || (log.user_id ? `#${log.user_id}` : '—')}
                          </td>
                          <td className="sl-action-cell">{log.action}</td>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.endpoint || '—'}
                          </td>
                          <td>
                            {log.status_code
                              ? <span className={`sl-status-code ${getStatusClass(log.status_code)}`}>{log.status_code}</span>
                              : '—'}
                          </td>
                          <td>
                            {log.duration_ms != null
                              ? <span className={`sl-duration ${getDurationClass(log.duration_ms)}`}>{log.duration_ms}ms</span>
                              : '—'}
                          </td>
                        </tr>

                        {/* Expanded Detail Row */}
                        {expandedId === log.id && (
                          <tr className="sl-expanded-row">
                            <td colSpan={9}>
                              <div className="sl-detail-panel">
                                <div className="sl-detail-grid">
                                  <div>
                                    <p className="sl-detail-label">Message</p>
                                    <p className="sl-detail-value">{log.message || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="sl-detail-label">IP Address</p>
                                    <p className="sl-detail-value">{log.ip_address || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="sl-detail-label">User Agent</p>
                                    <p className="sl-detail-value" style={{ fontSize: '0.7rem' }}>{log.user_agent || '—'}</p>
                                  </div>
                                  {log.user_email && (
                                    <div>
                                      <p className="sl-detail-label">Email</p>
                                      <p className="sl-detail-value">{log.user_email}</p>
                                    </div>
                                  )}
                                  {log.property_id && (
                                    <div>
                                      <p className="sl-detail-label">Property ID</p>
                                      <p className="sl-detail-value">{log.property_id}</p>
                                    </div>
                                  )}
                                  {log.method && (
                                    <div>
                                      <p className="sl-detail-label">Method</p>
                                      <p className="sl-detail-value">{log.method}</p>
                                    </div>
                                  )}
                                </div>
                                {log.details && (
                                  <div>
                                    <p className="sl-detail-label">Details</p>
                                    <div className="sl-detail-json">{formatJson(log.details)}</div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="sl-pagination">
                  <button
                    className="sl-page-btn"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  <span className="sl-page-info">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    className="sl-page-btn"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="cs-empty-state">
              <FaServer style={{ fontSize: '2rem', color: 'var(--text-2)' }} />
              <p>No log entries found</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '-0.5rem' }}>Try adjusting your filters or date range</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SystemLogs;
